import { SchmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { fromEvent } from 'rxjs'
import { throttleTime } from 'rxjs/operators'
import '../button/icon-button'
import '../slide-indicator/slide-indicator'

@customElement('schmancy-slider')
export class SchmancySlider extends SchmancyElement {
	static styles = [
		css`
			.slider {
				display: flex;
				overflow-x: auto;
				scroll-snap-type: x mandatory;
				-ms-overflow-style: none;
				scrollbar-width: none;
			}
			.slider::-webkit-scrollbar {
				display: none;
			}

			::slotted(schmancy-slide) {
				flex: 0 0 100%;
				box-sizing: border-box;
			}
		`,
	]

	@state() private selectedIndex: number = 0

	@property({ type: Boolean }) showArrows: boolean = true

	@query('#slider') private slider!: HTMLDivElement
	@query('slot') private defaultSlot!: HTMLSlotElement

	protected firstUpdated() {
		this.slider.scrollLeft = 0

		fromEvent(this.slider, 'scroll')
			.pipe(throttleTime(100, undefined, { trailing: true }))
			.subscribe(() => {
				this.updateSelectedIndexOnScroll()
			})
	}

	private updateSelectedIndexOnScroll() {
		const slides = this.defaultSlot?.assignedElements({ flatten: true }) ?? []
		if (!slides.length) return

		const oldIndex = this.selectedIndex

		const sliderCenter = this.slider.scrollLeft + this.slider.clientWidth / 2

		let closestIndex = 0
		let closestDistance = Infinity

		slides.forEach((slide, index) => {
			const itemStart = (slide as HTMLElement).offsetLeft
			const itemCenter = itemStart + slide.clientWidth / 2
			const distance = Math.abs(sliderCenter - itemCenter)

			if (distance < closestDistance) {
				closestDistance = distance
				closestIndex = index
			}
		})

		this.selectedIndex = closestIndex

		if (this.selectedIndex !== oldIndex) {
			this.dispatchEvent(
				new CustomEvent('slide-changed', {
					detail: { index: this.selectedIndex },
				}),
			)
		}
	}

	private goToSlide(newIndex: number) {
		const slides = this.defaultSlot?.assignedElements({ flatten: true }) ?? []
		if (!slides[newIndex]) return

		this.slider.scrollTo({
			left: (slides[newIndex] as HTMLElement).offsetLeft,
			behavior: 'smooth',
		})
	}

	private onPrevClick() {
		this.goToSlide(this.selectedIndex - 1)
	}

	private onNextClick() {
		const slides = this.defaultSlot?.assignedElements({ flatten: true }) ?? []
		if (this.selectedIndex < slides.length - 1) {
			this.goToSlide(this.selectedIndex + 1)
		}
	}

	render() {
		const slides = this.defaultSlot?.assignedElements({ flatten: true }) ?? []

		return html`
			<div class="relative inset-0">
				<div class="slider" id="slider">
					<!-- render() counts the slides off this slot, and the first render runs
					     before anything is assigned to it: without this the carousel paints
					     no indicator and an enabled "next" chevron on a set it thinks is
					     empty, until the first scroll happens to re-render it. -->
					<slot @slotchange=${() => this.requestUpdate()}></slot>
				</div>

				${
					this.showArrows
						? html`
								<schmancy-icon-button
									class="absolute left-2 top-1/2 -translate-y-1/2"
									@click=${this.onPrevClick}
									?disabled=${this.selectedIndex === 0}
								>
									chevron_left
								</schmancy-icon-button>
								<schmancy-icon-button
									class="absolute right-2 top-1/2 -translate-y-1/2"
									@click=${this.onNextClick}
									?disabled=${this.selectedIndex === slides.length - 1}
								>
									chevron_right
								</schmancy-icon-button>
							`
						: null
				}

				<schmancy-slide-indicator
					class="absolute bottom-4 left-1/2 -translate-x-1/2"
					.value=${this.selectedIndex + 1}
					.max=${slides.length}
				></schmancy-slide-indicator>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-slider': SchmancySlider
	}
}
